import { Numeric, Select } from "@rothoblaas/design-system";
import { FC } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import useI18n from "../../../../../hooks/useTranslations";
import { DEFAULT_JOIST_TOP_ALIGNMENT } from "../../../../../http/lock_calculator/types";
import styles from "../../LockCalculator.module.css";
import type { LockCalculatorFormValues } from "../../model/form";
import { applyCascade } from "../notices/applyCascade";
import { NoticeFormField as FormField } from "../notices/NoticeFormField";
import { useNotices } from "../notices/NoticesContext";

type AlignmentOption = { value: "aligned" | "offset"; label: string };

interface JoistTopFieldsProps {
  readOnly?: boolean;
  onOpenHelp?: (text: string, image?: string) => void;
}

/**
 * Where the joist and the connector sit under the header top: the joist's position (column header only),
 * the connector's distance from the joist top and, with the joist offset, the offset from the column top.
 */
export const JoistTopFields: FC<JoistTopFieldsProps> = ({ readOnly = false, onOpenHelp }) => {
  const { translation: t } = useI18n();
  const form = useFormContext<LockCalculatorFormValues>();
  const {
    control,
    formState: { errors },
  } = form;
  const { reportCascade } = useNotices();
  // Clearing the connector distance or the joist offset goes back a step: the
  // chosen locks were picked against them, so the connectors step is invalidated.
  // Only while the input has focus: Numeric also emits null when the form is
  // reloaded from the server (a default is stored as unset), which isn't a clear.
  const handleTopFieldChange = (
    onChange: (value: number | null | undefined) => void,
    value: number | null | undefined,
    inputId: string,
    field: "joistMember.connectorTopDistance" | "joistMember.topOffsetFromHeader",
  ) => {
    onChange(value);
    if (value == null && document.activeElement?.id === inputId) {
      applyCascade(form, reportCascade, {
        code: "connectorsInvalidated",
        causes: [field],
        effect: "connectorPairs.0.lockItemId",
        next: null,
      });
      applyCascade(form, reportCascade, {
        code: "connectorsInvalidated",
        causes: [field],
        effect: "connectorPairs.1.lockItemId",
        next: null,
        silent: true,
      });
    }
  };
  const [headerOrientation, topAlignment] = useWatch({
    control,
    name: ["headerMember.orientation", "joistMember.topAlignment"],
  });
  const alignmentOptions: AlignmentOption[] = [
    { value: "aligned", label: t.lockCalculatorModule?.timber?.joistTopAligned ?? "aligned" },
    { value: "offset", label: t.lockCalculatorModule?.timber?.joistTopOffsetOption ?? "offset" },
  ];

  return (
    <div className={styles.compactFormStack}>
      {headerOrientation === "column" && (
        <Controller
          name="joistMember.topAlignment"
          control={control}
          render={({ field }) => (
            <FormField
              name="joistMember.topAlignment"
              label={t.lockCalculatorModule?.timber?.joistTopAlignment}
              error={errors.joistMember?.topAlignment}
              showHelp={{
                tooltip: t.lockCalculatorModule?.helpTexts?.joistTopAlignment,
                onOpen: () => onOpenHelp?.(t.lockCalculatorModule?.helpTexts?.joistTopAlignment),
              }}
            >
              {({ labelId, isInvalid }) => (
                <Select<AlignmentOption, false>
                  inputId={labelId}
                  value={alignmentOptions.find((o) => o.value === field.value) ?? null}
                  options={alignmentOptions}
                  getOptionValue={(opt) => opt.value}
                  getOptionLabel={(opt) => opt.label}
                  isDisabled={readOnly}
                  isInvalid={isInvalid}
                  onChange={(selected) => field.onChange(selected?.value ?? DEFAULT_JOIST_TOP_ALIGNMENT)}
                />
              )}
            </FormField>
          )}
        />
      )}

      <Controller
        name="joistMember.connectorTopDistance"
        control={control}
        rules={{ min: 0 }}
        render={({ field }) => (
          <FormField
            name="joistMember.connectorTopDistance"
            label={t.lockCalculatorModule?.timber?.connectorTopDistance}
            error={errors.joistMember?.connectorTopDistance}
            isOptional
            showHelp={{
              tooltip: t.lockCalculatorModule?.helpTexts?.connectorTopDistance,
              onOpen: () => onOpenHelp?.(t.lockCalculatorModule?.helpTexts?.connectorTopDistance),
            }}
          >
            {({ labelId, isInvalid }) => (
              <Numeric
                {...field}
                value={field.value}
                decimalScale={1}
                suffix={t.common.unitMm}
                id={labelId}
                isInvalid={isInvalid}
                allowNegative={false}
                disabled={readOnly}
                onValueChange={(value) =>
                  handleTopFieldChange(field.onChange, value, labelId, "joistMember.connectorTopDistance")
                }
              />
            )}
          </FormField>
        )}
      />

      {headerOrientation === "column" && topAlignment === "offset" && (
        <Controller
          name="joistMember.topOffsetFromHeader"
          control={control}
          rules={{ min: 0 }}
          render={({ field }) => (
            <FormField
              name="joistMember.topOffsetFromHeader"
              label={t.lockCalculatorModule?.timber?.joistTopOffset}
              error={errors.joistMember?.topOffsetFromHeader}
              isOptional
              showHelp={{
                tooltip: t.lockCalculatorModule?.helpTexts?.joistTopOffset,
                onOpen: () => onOpenHelp?.(t.lockCalculatorModule?.helpTexts?.joistTopOffset),
              }}
            >
              {({ labelId, isInvalid }) => (
                <Numeric
                  {...field}
                  value={field.value}
                  decimalScale={1}
                  suffix={t.common.unitMm}
                  id={labelId}
                  isInvalid={isInvalid}
                  allowNegative={false}
                  disabled={readOnly}
                  onValueChange={(value) =>
                    handleTopFieldChange(field.onChange, value, labelId, "joistMember.topOffsetFromHeader")
                  }
                />
              )}
            </FormField>
          )}
        />
      )}
    </div>
  );
};
